import * as React from "react";
import { Link } from "react-router-dom";

import {
  acceptConnection,
  declineConnection,
  revokeConnection,
  listConnections,
  searchMentionCandidates,
  sendConnectRequest,
} from "@/api/client";
import { ConnectionView, HANDLE_REGEX, MentionCandidate } from "@/api/types";
import { PageTitle } from "@/components/PageTitle";
import { useFeature } from "@/features/profile/useFeature";

/** Strip a leading @ and lowercase — the form the backend validates + stores. */
function normalizeHandle(raw: string): string {
  return raw.trim().replace(/^@+/, "").toLowerCase();
}

/**
 * Manage connections: send a connect request to a @handle, and respond to / review
 * requests. A2A scheduling is gated on being connected, so this is where a user
 * grows the set of people they can book with.
 */
export function ConnectionsPage() {
  const [received, setReceived] = React.useState<ConnectionView[]>([]);
  const [sent, setSent] = React.useState<ConnectionView[]>([]);
  const [accepted, setAccepted] = React.useState<ConnectionView[]>([]);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    const [r, s, a] = await Promise.all([
      listConnections("received"),
      listConnections("sent"),
      listConnections("accepted"),
    ]);
    setReceived(r);
    setSent(s);
    setAccepted(a);
    setLoading(false);
  }, []);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const respond = async (id: string, accept: boolean) => {
    await (accept ? acceptConnection(id) : declineConnection(id));
    await refresh();
  };

  // Confirmed, because it is not obviously undoable: the other side has to ask again for the
  // connection to come back, and they may not.
  const revoke = async (id: string, peer: string | null) => {
    const who = peer ? `@${peer}` : "this person";
    if (!window.confirm(`Remove your connection with ${who}? They would have to ask again.`)) return;
    await revokeConnection(id);
    await refresh();
  };

  const canConnect = useFeature("AI_SCHEDULING") === "granted";

  const pendingReceived = received.filter((c) => c.status === "pending");

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-6">
      <header>
        <PageTitle>Connections</PageTitle>
      </header>

      {/* The one paid part of this page. Everything below — seeing who you are connected to,
          answering a request, removing a connection — is open to every plan, because V35 created
          connections nobody agreed to and getting out of one cannot be something you buy. */}
      {canConnect ? (
        <ConnectBox onSent={refresh} />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-muted/20 px-4 py-3">
          <span className="text-sm text-foreground">Connecting with someone new is on Pro.</span>
          <Link to="/upgrade" className="text-sm font-semibold text-brand-blue hover:underline">
            See plans
          </Link>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <Section title={`Requests received${pendingReceived.length ? ` (${pendingReceived.length})` : ""}`}>
            {received.length === 0 && <Empty>No one has asked to connect yet.</Empty>}
            {received.map((c) => (
              <Row key={c.id} connection={c}>
                {c.status === "pending" ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => respond(c.id, true)}
                      className="rounded-md border-2 border-black bg-brand-blue px-3 py-1 text-xs font-semibold text-white hover:bg-brand-blue/90"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => respond(c.id, false)}
                      className="rounded-md border-2 border-black px-3 py-1 text-xs font-semibold hover:bg-muted"
                    >
                      Decline
                    </button>
                  </div>
                ) : (
                  <StatusBadge status={c.status} />
                )}
              </Row>
            ))}
          </Section>

          <Section title="Requests sent">
            {sent.length === 0 && <Empty>You haven't sent any requests.</Empty>}
            {sent.map((c) => (
              <Row key={c.id} connection={c}>
                <StatusBadge status={c.status} />
              </Row>
            ))}
          </Section>

          <Section title={`Connected${accepted.length ? ` (${accepted.length})` : ""}`}>
            {accepted.length === 0 && <Empty>You're not connected with anyone yet.</Empty>}
            {accepted.map((c) => (
              <Row key={c.id} connection={c}>
                <span className="text-xs text-muted-foreground">Bookable</span>
                <button
                  type="button"
                  onClick={() => void revoke(c.id, c.peerHandle)}
                  className="rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-muted/50"
                >
                  Remove
                </button>
              </Row>
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

/** The @handle input: validate format, autocomplete discoverable handles, send. */
function ConnectBox({ onSent }: { onSent: () => Promise<void> }) {
  const [value, setValue] = React.useState("");
  const [suggestions, setSuggestions] = React.useState<MentionCandidate[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState<string | null>(null);

  const normalized = normalizeHandle(value);
  const formatOk = HANDLE_REGEX.test(normalized);

  // Autocomplete over DISCOVERABLE handles (private users simply won't appear;
  // you can still type their full handle and send).
  React.useEffect(() => {
    if (normalized.length < 1) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      searchMentionCandidates(normalized, 5)
        .then((cands) => {
          if (!cancelled) setSuggestions(cands);
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [normalized]);

  const submit = async () => {
    if (!formatOk || busy) return;
    setBusy(true);
    setNote(null);
    try {
      await sendConnectRequest(normalized);
      // Uniform acknowledgement — we never reveal whether the handle exists.
      setNote(`Request sent to @${normalized}. If they accept, you'll be able to book with them.`);
      setValue("");
      setSuggestions([]);
      await onSent();
    } catch {
      setNote("Couldn't send that request. Check the handle and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border-2 border-black bg-background p-4">
      <p className="font-comic-headline text-sm uppercase">Connect with someone</p>
      <p className="mb-2 text-xs text-muted-foreground">
        Enter their @handle. You can only schedule with people you're connected to.
      </p>
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            @
          </span>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="handle"
            className="w-full rounded-md border-2 border-black bg-background py-2 pl-7 pr-3 text-sm"
            aria-label="Handle to connect with"
          />
          {suggestions.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border-2 border-black bg-background shadow">
              {suggestions.map((s) => (
                <li key={s.handle}>
                  <button
                    onClick={() => {
                      setValue(s.handle);
                      setSuggestions([]);
                    }}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
                  >
                    <span>@{s.handle}</span>
                    <span className="text-xs text-muted-foreground">{s.display_name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          onClick={submit}
          disabled={!formatOk || busy}
          className="rounded-md border-2 border-black bg-brand-blue px-4 py-2 text-xs font-semibold uppercase text-white hover:bg-brand-blue/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send request"}
        </button>
      </div>
      {value.length > 0 && !formatOk && (
        <p className="mt-1 text-xs text-red-600">
          Handles are 3–30 characters: lowercase letters and numbers.
        </p>
      )}
      {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="font-comic-headline text-sm uppercase tracking-wide">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Row({ connection, children }: { connection: ConnectionView; children: React.ReactNode }) {
  const label = connection.peerHandle ? `@${connection.peerHandle}` : "(unknown)";
  return (
    <div className="flex items-center justify-between rounded-md border-2 border-black bg-background px-3 py-2">
      <div className="text-sm">
        <span className="font-semibold">{label}</span>
        {connection.peerDisplayName && (
          <span className="ml-2 text-xs text-muted-foreground">{connection.peerDisplayName}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function StatusBadge({ status }: { status: ConnectionView["status"] }) {
  const styles: Record<ConnectionView["status"], string> = {
    pending: "bg-amber-100 text-amber-800",
    accepted: "bg-emerald-100 text-emerald-800",
    declined: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-semibold capitalize ${styles[status]}`}>
      {status}
    </span>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}
